import { useEffect, useState } from "react";
import LogoDefault from "../../../../../components/logos/logo.svg";
import ResetIcon from "../../../../../images/icons/resetIcon.png";
import TicketingSettingsModal from "./TicketingSettingsModal";

function ThemeOption({ darkMode, t, updateState }) {
  return (
    <div className="darkModeOption">
      <p className="sectionTitle">{t("chooseTheme")}</p>

      <div className="buttonsContainer">
        <button
          type="button"
          className={`${darkMode ? "active" : ""}`}
          onClick={() => updateState("darkMode", true)}
        >
          {t("darkMode")}
        </button>
        <button
          type="button"
          className={`${darkMode ? "" : "active"}`}
          onClick={() => updateState("darkMode", false)}
        >
          {t("lightMode")}
        </button>
      </div>
    </div>
  );
}

function LogoOption({ event, handleLogoChange, logoFileInputRef, t, updateState }) {
  return (
    <div className="customLogoOption">
      <p className="sectionTitle">{t("chooseLogo")}</p>

      <div className="customLogoOptionContent">
        <div className="buttonsContainer">
          <button type="button" onClick={() => logoFileInputRef.current.click()}>
            {t("changeImage")}
          </button>
          {event?.logoUrl && (
            <button
              type="button"
              className="resetButton"
              onClick={() => updateState("logoUrl", null)}
            >
              <img src={ResetIcon} alt={t("reset")} />
            </button>
          )}
        </div>
        <div className="logoPreview">
          <img src={event?.logoUrl ? event.logoUrl : LogoDefault} alt="Logo" />
        </div>

        <input
          type="file"
          accept="image/png"
          ref={logoFileInputRef}
          onChange={handleLogoChange}
          style={{ display: "none" }}
        />
      </div>
    </div>
  );
}

const normalizeHexColor = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";
  const withHash = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
  return withHash.toLowerCase();
};

const isValidHexColor = (value) => /^#[0-9a-f]{6}$/i.test(value);

function ColorOption({ color, defaultColor, label, onChange, onPick, onReset, t }) {
  const resolvedColor = color || defaultColor;
  const [hexValue, setHexValue] = useState(resolvedColor);
  const [hexError, setHexError] = useState("");

  useEffect(() => {
    setHexValue(resolvedColor);
    setHexError("");
  }, [resolvedColor]);

  const applyHexValue = () => {
    const normalized = normalizeHexColor(hexValue);
    if (!isValidHexColor(normalized)) {
      setHexError(t("invalidHexColor"));
      return;
    }

    setHexError("");
    setHexValue(normalized);
    onChange(normalized);
  };

  return (
    <div className="color">
      <div className="buttonsContainer">
        <button type="button" onClick={onPick}>
          {label}
        </button>
        {color !== defaultColor && (
          <button type="button" className="resetButton" onClick={onReset}>
            <img src={ResetIcon} alt={t("reset")} />
          </button>
        )}
      </div>
      <div className="colorManualControl">
        <span className="color-preview" style={{ backgroundColor: resolvedColor }}></span>
        <label>
          {t("hexColorLabel")}
          <input
            type="text"
            value={hexValue}
            placeholder="#4200ff"
            onBlur={applyHexValue}
            onChange={(event) => {
              setHexValue(event.target.value);
              if (hexError) setHexError("");
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                applyHexValue();
              }
            }}
          />
        </label>
        {hexError && <small className="colorHexError">{hexError}</small>}
      </div>
    </div>
  );
}

function MainColorsOption({
  defaultPrimaryColor,
  defaultSecondaryColor,
  event,
  setActiveColor,
  t,
  updateState,
}) {
  return (
    <div className="mainColorsOption">
      <p className="sectionTitle">{t("chooseMainColors")}</p>
      <div className="colors">
        <ColorOption
          color={event?.mainColor1}
          defaultColor={defaultPrimaryColor}
          label={t("mainColor1")}
          onChange={(value) => updateState("mainColor1", value)}
          onPick={() => setActiveColor("color1")}
          onReset={() => updateState("mainColor1", defaultPrimaryColor)}
          t={t}
        />
        <ColorOption
          color={event?.mainColor2}
          defaultColor={defaultSecondaryColor}
          label={t("mainColor2")}
          onChange={(value) => updateState("mainColor2", value)}
          onPick={() => setActiveColor("color2")}
          onReset={() => updateState("mainColor2", defaultSecondaryColor)}
          t={t}
        />
      </div>
    </div>
  );
}

function VisualSettingsModal({
  defaultPrimaryColor,
  defaultSecondaryColor,
  event,
  handleLogoChange,
  logoFileInputRef,
  onClose,
  setActiveColor,
  t,
  updateState,
}) {
  return (
    <TicketingSettingsModal
      className="visual"
      description={t("visualSettingsDescription")}
      eyebrow={t("visualSettingsEyebrow")}
      labelledBy="ticketing-visual-settings-title"
      onClose={onClose}
      t={t}
      title={t("formVisualCustomization")}
    >
      <div className="ticketingSettingsModalBody customOptions">
        <ThemeOption darkMode={event?.darkMode} t={t} updateState={updateState} />
        <LogoOption
          event={event}
          handleLogoChange={handleLogoChange}
          logoFileInputRef={logoFileInputRef}
          t={t}
          updateState={updateState}
        />
        <MainColorsOption
          defaultPrimaryColor={defaultPrimaryColor}
          defaultSecondaryColor={defaultSecondaryColor}
          event={event}
          setActiveColor={setActiveColor}
          t={t}
          updateState={updateState}
        />
      </div>
    </TicketingSettingsModal>
  );
}

export default VisualSettingsModal;
